import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { ImagePlus, Trash2, Star, X, Tag, Loader2, ChevronLeft, ShoppingBag, Sparkles } from "lucide-react";

function StarPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1.5" data-testid="post-rating-picker">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(value === s ? null : s)}
          className="p-0.5"
          data-testid={`post-rating-${s}`}
          aria-label={`Rate ${s} of 5`}
        >
          <Star className={`w-7 h-7 ${value && s <= value ? "fill-amber-400 text-amber-400" : "text-zinc-700"}`} />
        </button>
      ))}
      {value != null && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="ml-2 text-xs text-zinc-400 hover:text-white"
          data-testid="post-rating-clear"
        >
          clear
        </button>
      )}
    </div>
  );
}

export default function CreatePost() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialProductId = params.get("product_id") || "";

  const [caption, setCaption] = useState("");
  const [captionIdeas, setCaptionIdeas] = useState([]);
  const [fetchingCaptions, setFetchingCaptions] = useState(false);
  const [media, setMedia] = useState([]); // [{path, type}]
  const [uploading, setUploading] = useState(false);
  const [rating, setRating] = useState(null);
  const [productId, setProductId] = useState(initialProductId);
  const [tagQuery, setTagQuery] = useState("");
  const [tagResults, setTagResults] = useState([]);
  const [taggedVendors, setTaggedVendors] = useState([]); // [{user_id, name}]
  const [orders, setOrders] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orders", { params: { role: "buyer" } });
        setOrders(data);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  // Product choices from past COMPLETED orders only
  const productChoices = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const o of orders) {
      if (o.status !== "completed") continue;
      for (const it of o.items || []) {
        if (!seen.has(it.product_id)) {
          seen.add(it.product_id);
          out.push({ ...it, vendor_id: o.vendor_id, vendor_name: o.vendor_name });
        }
      }
    }
    return out;
  }, [orders]);

  // Vendors the buyer has at least one COMPLETED order with — these are the only
  // vendors they're allowed to tag in a community post.
  const eligibleVendorIds = useMemo(() => {
    const ids = new Set();
    for (const o of orders) {
      if (o.status === "completed" && o.vendor_id) ids.add(o.vendor_id);
    }
    return ids;
  }, [orders]);

  const hasAnyCompletedOrder = eligibleVendorIds.size > 0;

  useEffect(() => {
    if (!tagQuery.trim()) {
      setTagResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { q: tagQuery, limit: 12 } });
        const uniq = new Map();
        for (const p of data) {
          // Only show vendors the buyer has a completed order with
          if (!eligibleVendorIds.has(p.vendor_id)) continue;
          if (!uniq.has(p.vendor_id)) uniq.set(p.vendor_id, { user_id: p.vendor_id, name: p.vendor_name });
        }
        setTagResults([...uniq.values()].filter((v) => !taggedVendors.find((t) => t.user_id === v.user_id)));
      } catch {
        setTagResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [tagQuery, taggedVendors, eligibleVendorIds]);

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMedia((m) => [...m, { path: data.path, type: f.type.startsWith("video") ? "video" : "image" }]);
      }
      toast.success("Media uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeMedia = (idx) => setMedia((m) => m.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!media.length && !caption.trim()) {
      toast.error("Add a photo, video, or caption");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        caption: caption.trim(),
        media_paths: media.map((m) => m.path),
        media_types: media.map((m) => m.type),
        tagged_vendor_ids: taggedVendors.map((v) => v.user_id),
        product_id: productId || null,
        rating,
      };
      await api.post("/community/posts", payload);
      toast.success("Posted!");
      navigate("/community");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't post");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  // Gate: users with no completed orders cannot create a community post.
  // Admins/master_admin are exempt (privileged).
  const isPrivileged = user.role === "admin" || user.role === "master_admin";
  if (!isPrivileged && !hasAnyCompletedOrder) {
    return (
      <div className="max-w-xl mx-auto px-4 py-10 space-y-5 text-center" data-testid="create-post-locked">
        <div className="mx-auto w-16 h-16 rounded-full bg-pink-500/15 grid place-items-center border border-pink-400/30">
          <ShoppingBag className="w-8 h-8 text-pink-300" />
        </div>
        <div>
          <h1 className="text-3xl font-black tracking-tighter">Order first, post next</h1>
          <p className="text-sm text-zinc-400 mt-2">
            Community posts are reserved for buyers who've actually tried something. Place an order, mark it completed,
            and we'll unlock posting + reviews + comments for that vendor.
          </p>
        </div>
        <div className="flex gap-2 justify-center">
          <button
            onClick={() => navigate("/feed")}
            className="h-11 px-5 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
            data-testid="create-post-locked-shop"
          >
            Discover gems
          </button>
          <button
            onClick={() => navigate("/orders")}
            className="h-11 px-5 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold"
            data-testid="create-post-locked-orders"
          >
            My orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-2xl mx-auto px-4 py-5 space-y-5 pb-24" data-testid="create-post-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white"
        data-testid="create-post-back"
      >
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div>
        <h1 className="text-3xl font-black tracking-tighter">Share a moment</h1>
        <p className="text-sm text-zinc-400 mt-1">Photos, videos, and the vendors who made it happen.</p>
      </div>

      {/* Media uploader */}
      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-2">
          {media.map((m, i) => (
            <div key={i} className="relative aspect-square rounded-2xl overflow-hidden bg-zinc-900 border border-white/10" data-testid={`create-post-media-${i}`}>
              {m.type === "video" ? (
                <video src={fileUrl(m.path)} className="w-full h-full object-cover" muted />
              ) : (
                <img src={fileUrl(m.path)} alt="" className="w-full h-full object-cover" />
              )}
              <button
                type="button"
                onClick={() => removeMedia(i)}
                className="absolute top-1.5 right-1.5 h-7 w-7 grid place-items-center rounded-full bg-black/60 hover:bg-red-500 text-white"
                data-testid={`create-post-remove-${i}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <label className="aspect-square rounded-2xl border-2 border-dashed border-white/15 hover:border-pink-400 grid place-items-center text-zinc-400 hover:text-pink-300 cursor-pointer transition-colors" data-testid="create-post-add-media">
            {uploading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <div className="flex flex-col items-center gap-1">
                <ImagePlus className="w-6 h-6" />
                <span className="text-[10px] font-bold uppercase tracking-widest">Add</span>
              </div>
            )}
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFile}
              className="hidden"
              data-testid="create-post-file-input"
            />
          </label>
        </div>
      </div>

      {/* Caption */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Caption</label>
          {media.length > 0 && (
            <button
              type="button"
              onClick={async () => {
                if (fetchingCaptions) return;
                setFetchingCaptions(true);
                try {
                  const first = media[0];
                  const path = first.path;
                  const url = path.startsWith("http") ? path : `${process.env.REACT_APP_BACKEND_URL}${path.startsWith("/api/files/") ? path : `/api/files/${path}`}`;
                  const { data } = await api.post("/ai/caption", { image_url: url, context: caption || "" });
                  setCaptionIdeas(data.items || []);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Couldn't generate captions");
                } finally {
                  setFetchingCaptions(false);
                }
              }}
              disabled={fetchingCaptions}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-gradient-to-r from-pink-500/20 to-cyan-500/20 hover:from-pink-500/30 hover:to-cyan-500/30 border border-pink-400/30 text-white text-[11px] font-black"
              data-testid="ai-caption-ideas-btn"
            >
              {fetchingCaptions ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {fetchingCaptions ? "Thinking…" : "AI caption ideas"}
            </button>
          )}
        </div>
        <Textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="What did you eat? Tell the story…"
          className="bg-white/5 border-white/10 rounded-2xl text-white min-h-[100px]"
          data-testid="create-post-caption"
        />
        {captionIdeas.length > 0 && (
          <div className="space-y-2 mt-2" data-testid="ai-caption-ideas-list">
            <p className="text-[10px] uppercase tracking-widest text-pink-300 font-black">AI suggestions — tap to use</p>
            {captionIdeas.map((idea, i) => (
              <button
                type="button"
                key={i}
                onClick={() => {
                  const tags = (idea.hashtags || []).map((h) => `#${h}`).join(" ");
                  setCaption(tags ? `${idea.caption}\n\n${tags}` : idea.caption);
                  toast.success("Caption applied");
                }}
                className="w-full text-left rounded-2xl border border-pink-400/20 bg-pink-500/[0.06] p-3 hover:border-pink-400/50 transition-colors"
                data-testid={`ai-caption-idea-${i}`}
              >
                <p className="text-sm text-zinc-100">{idea.caption}</p>
                {idea.hashtags?.length > 0 && (
                  <p className="text-[11px] text-pink-300 mt-1">
                    {idea.hashtags.map((h) => `#${h}`).join(" ")}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Star rating */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-2">
        <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Rate the experience (optional)</p>
        <StarPicker value={rating} onChange={setRating} />
      </div>

      {/* Attach product from past orders */}
      {productChoices.length > 0 && (
        <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
          <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Tag a purchase (optional)</p>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
            {productChoices.slice(0, 12).map((p) => (
              <button
                key={p.product_id}
                type="button"
                onClick={() => setProductId(productId === p.product_id ? "" : p.product_id)}
                className={`shrink-0 w-32 rounded-2xl border p-2 text-left transition-colors ${
                  productId === p.product_id
                    ? "bg-pink-500/15 border-pink-400/40"
                    : "bg-white/5 border-white/10 hover:bg-white/10"
                }`}
                data-testid={`create-post-product-${p.product_id}`}
              >
                {p.image_paths?.[0] && (
                  <img src={fileUrl(p.image_paths[0])} alt={p.name} className="w-full aspect-square rounded-xl object-cover mb-1.5" />
                )}
                <p className="text-xs font-bold truncate">{p.name}</p>
                <p className="text-[10px] text-zinc-400 truncate">@{p.vendor_name}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tag vendors */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Tag vendors</p>
        <div className="relative">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={tagQuery}
            onChange={(e) => setTagQuery(e.target.value)}
            placeholder="Search a vendor or product name…"
            className="pl-9 h-11 bg-white/5 border-white/10 rounded-full text-white"
            data-testid="create-post-tag-input"
          />
        </div>
        {tagResults.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="create-post-tag-results">
            {tagResults.map((v) => (
              <button
                key={v.user_id}
                type="button"
                onClick={() => {
                  setTaggedVendors((arr) => [...arr, v]);
                  setTagQuery("");
                  setTagResults([]);
                }}
                className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full bg-cyan-400/15 text-cyan-200 border border-cyan-400/30 hover:bg-cyan-400/25"
                data-testid={`create-post-tag-add-${v.user_id}`}
              >
                + @{v.name}
              </button>
            ))}
          </div>
        )}
        {taggedVendors.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="create-post-tagged-list">
            {taggedVendors.map((v) => (
              <span
                key={v.user_id}
                className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-full bg-pink-500/15 text-pink-200 border border-pink-400/30"
                data-testid={`create-post-tagged-${v.user_id}`}
              >
                @{v.name}
                <button
                  type="button"
                  onClick={() => setTaggedVendors((arr) => arr.filter((x) => x.user_id !== v.user_id))}
                  className="ml-1 text-pink-200 hover:text-white"
                  data-testid={`create-post-untag-${v.user_id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <Button
        type="submit"
        disabled={submitting || uploading}
        className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
        data-testid="create-post-submit"
      >
        {submitting ? "Posting…" : "Share post"}
      </Button>
    </form>
  );
}
